import { api } from './api';

export type PushState =
  | { status: 'unsupported' }
  | { status: 'needs_install' } // not running as standalone PWA
  | { status: 'denied' }
  | { status: 'idle' } // supported, standalone, not yet subscribed
  | { status: 'subscribed'; endpoint: string };

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia === 'function') {
    try {
      if (window.matchMedia('(display-mode: standalone)').matches) return true;
    } catch {
      // Sandboxed WebViews can throw on unsupported media queries; fall through to the iOS check.
    }
  }
  return Boolean((window.navigator as { standalone?: boolean }).standalone);
}

export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua) && !('MSStream' in window)) return true;
  // iPadOS 13+ reports UA as Macintosh; distinguish from real Mac via touch.
  return (
    /Macintosh/.test(ua) &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1
  );
}

export function pushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export type PushDiagnostics = {
  isIOS: boolean;
  isStandalone: boolean;
  hasServiceWorker: boolean;
  hasPushManager: boolean;
  hasNotification: boolean;
  notificationPermission: NotificationPermission | 'unavailable';
  userAgent: string;
};

export function pushDiagnostics(): PushDiagnostics {
  const hasNotification = typeof window !== 'undefined' && 'Notification' in window;
  return {
    isIOS: isIOS(),
    isStandalone: isStandalone(),
    hasServiceWorker: typeof navigator !== 'undefined' && 'serviceWorker' in navigator,
    hasPushManager: typeof window !== 'undefined' && 'PushManager' in window,
    hasNotification,
    notificationPermission: hasNotification ? Notification.permission : 'unavailable',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  };
}

export async function getPushState(): Promise<PushState> {
  // iOS gate runs first: pre-16.4 Safari is missing Notification, so pushSupported()
  // would short-circuit to 'unsupported' and the user would never see the actionable
  // "add to home screen" hint.
  if (isIOS() && !isStandalone()) return { status: 'needs_install' };
  if (!pushSupported()) return { status: 'unsupported' };
  if (Notification.permission === 'denied') return { status: 'denied' };
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) return { status: 'subscribed', endpoint: sub.endpoint };
  return { status: 'idle' };
}

// urlBase64ToUint8Array — VAPID public key needs to be a Uint8Array<ArrayBuffer> for pushManager.subscribe.
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const buf = new ArrayBuffer(raw.length);
  const out = new Uint8Array(buf);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export async function subscribePush(): Promise<PushState> {
  if (isIOS() && !isStandalone()) return { status: 'needs_install' };
  if (!pushSupported()) return { status: 'unsupported' };

  // Always called from a user gesture handler (button click).
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return { status: 'denied' };

  const { key } = await fetch('/api/v1/push/vapid-public-key', { credentials: 'include' }).then((r) => r.json());
  if (!key) throw new Error('VAPID public key not configured on the server');

  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(key),
  });
  const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    throw new Error('PushSubscription missing fields');
  }
  await fetch('/api/v1/notifications/subscribe', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    }),
  });
  return { status: 'subscribed', endpoint: json.endpoint };
}

export async function unsubscribePush(): Promise<PushState> {
  if (!pushSupported()) return { status: 'unsupported' };
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    const endpoint = sub.endpoint;
    await sub.unsubscribe();
    try {
      await fetch('/api/v1/notifications/unsubscribe', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ endpoint }),
      });
    } catch {
      // best-effort
    }
  }
  return { status: 'idle' };
}

// Re-attach the existing subscription to the current user on every app open.
// iOS rotates push endpoints; this catches the rotation.
export async function refreshSubscription(): Promise<void> {
  const state = await getPushState();
  if (state.status !== 'subscribed') return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return;
  try {
    await fetch('/api/v1/notifications/subscribe', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      }),
    });
  } catch {
    // ignore — server will see stale subscription on next dispatch
  }
}

export async function sendTestPush() {
  return api.testPush();
}
